import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/lilia-ui/button";
import { cn } from "@/lib/cn";
import { IconX } from "@tabler/icons-react";
import { RefreshButton } from "@/components/shared/refreshButton/RefreshButton";
import type { Block } from "@/lib/api";
import { LaTeXPreview } from "./preview/LaTeXPreview";
import { LMLPreview } from "./preview/LMLPreview";
import { PdfPreview } from "./preview/PdfPreview";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface BibEntry {
  id: string;
  citeKey: string;
  entryType: string;
  fields: Record<string, string>;
}

// LILIA-137: dropped the "typst" (source) and "typst-preview" (rendered)
// tabs. The rendered tab was backed by a JS soft-renderer in
// `typst-worker.ts` that approximated Typst output without actually
// running the compiler — directives like #columns(...) rendered as
// literal text. The Document tab in /preview now shows the real
// Typst-compiled PDF (via /export/pdf), so the soft-renderer tab was
// duplicative and lied about what it could render. The "typst" source
// tab was unreachable through the tab UI anyway.
type Tab = "latex" | "lml" | "pdf";

// PDF first (output), then the markup tabs.
const ALL_TABS: readonly Tab[] = ["pdf", "latex", "lml"];

interface PreviewPanelProps {
  documentId: string;
  documentTitle?: string;
  isVisible: boolean;
  onClose: () => void;
  refreshKey?: number;
  blocks?: Block[];
  bibliographyEntries?: BibEntry[];
  activeTab?: Tab;
  onTabChange?: (tab: Tab) => void;
  /**
   * Which tabs this mount offers. Defaults to all three.
   *
   * The chat workspace promotes PDF and Source to top-level surfaces, so it
   * mounts this panel twice with one tab and two — a strip repeating the tab
   * you just clicked one row above is noise, and with a single tab there is
   * nothing to switch, so the strip is dropped entirely.
   */
  tabs?: readonly Tab[];
  focusedBlockId?: string | null;
  onScrollToBlock?: (blockId: string) => void;
  width?: number;
  height?: number;
  /**
   * Fill the parent flex cell (chat-first Primary artifact). Skips the
   * default 48%/max-60vw side-pane sizing and outer border so the host
   * chrome owns the frame.
   */
  fill?: boolean;
}

export function PreviewPanel({
  documentId,
  documentTitle,
  isVisible,
  onClose,
  refreshKey = 0,
  blocks = [],
  bibliographyEntries = [],
  activeTab: controlledTab,
  onTabChange,
  tabs = ALL_TABS,
  focusedBlockId,
  onScrollToBlock,
  width,
  height,
  fill = false,
}: PreviewPanelProps) {
  const { t } = useTranslation('editor');
  // PDF first — this pane is document output; LaTeX/LML are secondary tabs.
  const [internalTab, setInternalTab] = useState<Tab>("pdf");
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = onTabChange ?? setInternalTab;
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  // The active tab's fetch, so Refresh can show working → done for it.
  const [childBusy, setChildBusy] = useState(false);
  // A tab that unmounts mid-fetch never says it finished.
  useEffect(() => setChildBusy(false), [activeTab]);

  const combinedRefreshKey = refreshKey + localRefreshKey;

  // What auto-refresh watches. Derived from the content itself rather than
  // from the blocks array identity, because the array is replaced on renders
  // that changed nothing — and each of those would otherwise cost a compile.
  //
  // Only the fields that reach the PDF are included: id, type, order and
  // content. `updatedAt` is deliberately excluded, since a save that rewrote
  // no content would still move it and trigger a recompile of an identical
  // document.
  const contentSignature = useMemo(
    () =>
      JSON.stringify(
        blocks.map((b) => [b.id, b.type, b.sortOrder, b.content]),
      ),
    [blocks],
  );

  // Refresh is now a contract: auto-refresh on edit. handleRefresh kept for
  // the chrome Refresh control and ⌘K.
  const handleRefresh = useCallback(() => {
    setLocalRefreshKey((k) => k + 1);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isVisible) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const shellClass = fill
    ? "bg-[var(--lilia-bg)] flex flex-col flex-1 min-w-0 min-h-0 w-full h-full"
    : cn(
        "bg-[var(--lilia-bg)] flex flex-col shrink-0 border-[var(--lilia-border)]",
        height ? "w-full border-t" : "h-full border-l",
      );
  const shellStyle = fill
    ? undefined
    : height
      ? { height, minHeight: 150, maxHeight: "70vh" as const }
      : { width: width ?? "48%", minWidth: 320, maxWidth: "60vw" as const };

  return (
    <div data-testid="preview-panel" data-active-tab={activeTab} className={shellClass} style={shellStyle}>
      {/* Source-pane header — Pass 3 contract (LILIA-114, 2026-04-29):
          32px tab strip · 2px primary underline on active · sentence-case
          labels · right side has only "↳ Reveal" + Refresh + close.
          The old green Refresh pill and the bordered tab-segment with a
          solid-fill active state both violated the Quiet aesthetic. */}
      <div
        className={cn(
          "flex items-center justify-between px-3 bg-[var(--lilia-surface)] border-b border-[var(--lilia-border)]",
          fill ? "h-7" : "h-8",
        )}
      >
        <div className="flex items-stretch h-full">
          {(tabs.length > 1 ? tabs : []).map((tab) => {
            const label = tab === "pdf" ? "PDF"
              : tab === "latex" ? "LaTeX"
              : tab === "lml" ? "LML"
              : t(tab);
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                data-testid={`preview-tab-${tab}`}
                data-active={active ? "true" : "false"}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "relative px-3 text-[12px] font-medium transition-colors",
                  active
                    ? "text-[var(--lilia-text)]"
                    : "text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]",
                )}
              >
                {label}
                {active && (
                  <span
                    aria-hidden
                    className="absolute left-2 right-2 bottom-0 h-[2px] rounded-t bg-[var(--lilia-primary)]"
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="inline-flex items-center gap-2">
          {/* Simple Refresh (user direction 2026-06-28) — replaces "Reveal".
              Re-fetches the active tab; the PDF tab is manual-refresh-only,
              so this is its update affordance. */}
          <RefreshButton
            testId="preview-refresh"
            label="Refresh preview"
            text="Refresh"
            onRefresh={handleRefresh}
            busy={childBusy}
            iconSize={13}
          />
          {/* In fill (artifact) mode the host surface switch owns close;
              keep Esc via onClose but hide the redundant X. */}
          {!fill && (
            <Button
              variant="subtle"
              size="icon-sm"
              onClick={onClose}
              title={t('closePreviewEsc')}
            >
              <IconX size={14} />
            </Button>
          )}
        </div>
      </div>

      {activeTab === "latex" && (
        <LaTeXPreview
          documentId={documentId}
          refreshKey={combinedRefreshKey}
          focusedBlockId={focusedBlockId}
          onScrollToBlock={onScrollToBlock}
          onBusyChange={setChildBusy}
          blocks={blocks as unknown as Parameters<typeof LaTeXPreview>[0]["blocks"]}
        />
      )}

      {activeTab === "lml" && (
        <LMLPreview
          blocks={blocks}
          bibliographyEntries={bibliographyEntries}
          focusedBlockId={focusedBlockId}
          onScrollToBlock={onScrollToBlock}
        />
      )}

      {activeTab === "pdf" && (
        // PDF auto-refreshes again, debounced, and can be switched off from
        // the preview toolbar.
        //
        // It was manual-only from 2026-05-14, when auto-refresh on every edit
        // was burning typst compile cycles and felt jittery. Both causes are
        // now addressed rather than assumed away: the server caches on a hash
        // of the generated source, so a refresh that changes nothing costs a
        // 304 and no compile, and edits are debounced so a burst of typing is
        // one compile rather than one per keystroke. PdfPreview records every
        // refresh duration to localStorage so the question can be settled on
        // a distribution instead of an impression.
        <PdfPreview
          documentId={documentId}
          // combinedRefreshKey, not localRefreshKey. The parent bumps its key
          // when the document changed on the server — a restore, an applied
          // suggestion — and the LaTeX tab beside this one has always listened
          // to it. The PDF did not, so restoring a version with this tab open
          // left yesterday's PDF on screen until a page reload, and with
          // auto-refresh off there was nothing else to catch it.
          //
          // These are deliberate, rare events, not keystrokes, and the server
          // caches on a hash of the generated source — so the one that changed
          // nothing costs a 304 rather than a compile.
          refreshKey={combinedRefreshKey}
          contentSignature={contentSignature}
          onBusyChange={setChildBusy}
        />
      )}

    </div>
  );
}
